import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useUI } from '@/store/ui'
import {
  MissionsAPI,
  type Mission,
  type MissionDraft,
  type MissionStatus,
  type MissionStep,
} from '@/api/missions'

// Re-export the shared types so existing imports from '@/store/missions' keep working.
export type { Mission, MissionDraft, MissionPriority, MissionStatus, MissionStep, StepStatus } from '@/api/missions'

function titleFor(draft: MissionDraft) {
  if (draft.protocol === 'Capital network path') return `Open path to ${draft.target}`
  if (draft.protocol === 'Draft outreach') return `Draft outreach to ${draft.target}`
  if (draft.protocol === 'Research company') return `Research ${draft.target}`
  if (draft.protocol === 'Prepare meeting brief') return `Prepare brief for ${draft.target}`
  return draft.target
}

function stepsFor(protocol: string): MissionStep[] {
  if (protocol === 'Capital network path') {
    return [
      { id: 'Target defined', status: 'done' },
      { id: 'Enrich target', status: 'running' },
      { id: 'Find warm path', status: 'pending' },
      { id: 'Draft intro', status: 'pending' },
      { id: 'Track response', status: 'pending' },
    ]
  }
  if (protocol === 'Draft outreach') {
    return [
      { id: 'Context selected', status: 'done' },
      { id: 'Generate draft', status: 'running' },
      { id: 'Review tone', status: 'pending' },
      { id: 'Log touch', status: 'pending' },
    ]
  }
  if (protocol === 'Research company') {
    return [
      { id: 'Company selected', status: 'done' },
      { id: 'Collect signals', status: 'running' },
      { id: 'Resolve entities', status: 'pending' },
      { id: 'Produce brief', status: 'pending' },
    ]
  }
  return [
    { id: 'Scope set', status: 'done' },
    { id: 'Gather context', status: 'running' },
    { id: 'Review artifact', status: 'pending' },
  ]
}

// Backend-backed missions, exposed with the same interface the pages already use.
export function useMissions() {
  const { diwaUrl } = useUI()
  const api = new MissionsAPI(diwaUrl)
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['missions'], queryFn: () => api.list() })
  const missions: Mission[] = data ?? []

  function createMission(draft: MissionDraft) {
    api
      .create({
        title: titleFor(draft),
        goal: draft.goal,
        status: 'Active',
        priority: draft.priority,
        domain: draft.domain,
        target: draft.target,
        protocol: draft.protocol,
        steps: stepsFor(draft.protocol),
      })
      .then(() => qc.invalidateQueries({ queryKey: ['missions'] }))
  }

  function updateStatus(id: string, status: MissionStatus) {
    api.updateStatus(id, status).then(() => qc.invalidateQueries({ queryKey: ['missions'] }))
  }

  return { missions, createMission, updateStatus }
}
